import { useCallback, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Badge,
  Box,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  createColumnHelper,
  DataTable,
  DataTableCellContent,
  DataTableCellTrigger,
  DataTableColumnSettings,
  DataTableEmpty,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  ScrollArea,
  Spinner,
  Text,
  useIsMobile,
  type DataTableColumnDef,
  type SortingState,
} from "@salestrak/ui";
import { UserSelector } from "#app/components/entity-selectors";
import { EntityTag } from "#app/components/entity";
import { LocalGroupProvider, useGroupStates } from "#app/lib/hooks/group-query-provider";
import { PageHeader, PageHeaderActions, PageHeaderBreadcrumb } from "#app/components/page-header";

import { useTeamMembers, type TeamMember } from "#app/lib/hooks/use-team-members";
import { useTeamMutations } from "#app/lib/hooks/use-team-mutations";
import { useTeamDetail } from "#app/lib/hooks/use-team-detail";
import { useAppData, type TeamMembershipRole } from "#app/lib/data";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { AccessDenied } from "#app/components/permissions/require-permission";
import { checkPermission, usePermission } from "#app/lib/hooks/use-permission";

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

const h = createColumnHelper<TeamMember>();

type UseTeamMemberColumnsParams = {
  leadCount: number;
  onChangeRole: (member: TeamMember, newRole: TeamMembershipRole) => void;
};

// ---------------------------------------------------------------------------
// RoleMenuCell — matches DataTablePopoverCell pattern (fills cell, active border)
// ---------------------------------------------------------------------------

type RoleMenuCellProps = {
  member: TeamMember;
  isLastLead: boolean;
  onChangeRole: (member: TeamMember, newRole: TeamMembershipRole) => void;
};

function RoleMenuCell({ member, isLastLead, onChangeRole }: RoleMenuCellProps) {
  const [open, setOpen] = useState(false);
  return (
    // Base UI's Menu root renders no DOM node, so there is nothing to stretch
    // to the cell — the trigger fills the cell itself.
    <Menu open={open} onOpenChange={setOpen}>
      <MenuTrigger
        disabled={isLastLead}
        render={<DataTableCellTrigger disabled={isLastLead} active={open} />}
      >
        <DataTableCellContent>
          <Badge size="sm" variant={member.role === "lead" ? "success" : "secondary"}>
            <Text>{member.role === "lead" ? "Lead" : "Teammate"}</Text>
          </Badge>
        </DataTableCellContent>
      </MenuTrigger>
      <MenuPopup align="start">
        <MenuItem
          disabled={member.role === "lead"}
          onClick={() => onChangeRole(member, "lead")}
        >
          Lead
          {member.role === "lead" ? <Icon name="check" size="md" color="primary" /> : null}
        </MenuItem>
        <MenuItem
          disabled={member.role === "member"}
          onClick={() => onChangeRole(member, "member")}
        >
          Teammate
          {member.role === "member" ? <Icon name="check" size="md" color="primary" /> : null}
        </MenuItem>
      </MenuPopup>
    </Menu>
  );
}

function useTeamMemberColumns({
  leadCount,
  onChangeRole,
}: UseTeamMemberColumnsParams): DataTableColumnDef<TeamMember>[] {
  return useMemo(
    () => [
      h.accessor({
        accessorKey: "name",
        header: "Name",
        enableSorting: true,
        meta: { minWidth: 200, truncate: true },
        cell: ({ row }) => {
          const member = row.original;
          return (
            <DataTableCellContent>
              <EntityTag
                entity={{ kind: "user", id: member.userId, user: member }}
              />
            </DataTableCellContent>
          );
        },
      }),
      h.accessor({
        accessorKey: "email",
        header: "Email",
        enableSorting: true,
        meta: { minWidth: 300 },
        cell: ({ getValue }) => (
          <DataTableCellContent>
            <Text size="sm" variant="muted" truncate={1}>{getValue() as string}</Text>
          </DataTableCellContent>
        ),
      }),
      h.display({
        id: "role",
        header: "Roles",
        meta: { minWidth: 120 },
        cell: ({ row }) => (
          <RoleMenuCell
            member={row.original}
            isLastLead={row.original.role === "lead" && leadCount <= 1}
            onChangeRole={onChangeRole}
          />
        ),
      }),
      h.date({ accessorKey: "creationDate", header: "Joined" }),
    ],
    [leadCount, onChangeRole],
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function TeamMembersPage() {
  const { teamId } = Route.useParams();
  const isMobile = useIsMobile();
  const { push, replace } = useAppNavigate();
  const { currentUser } = useAppData();
  // A team lead can reach this page without `manage.teams` — the parent
  // breadcrumb targets (/settings/teams and the team settings page) are
  // manage.teams-only, so render them as plain text rather than links
  // that would dead-end them on the access-denied screen.
  const canManageTeams = usePermission("manage.teams");
  const { addTeamMembers, removeTeamMembers } = useTeamMutations();

  const [sorting, setSorting] = useState<SortingState>([{ id: "name", desc: false }]);

  const {
    teamName,
    teamMembers,
    existingUserIds,
    leadCount,
    loading,
    error,
    refetch,
  } = useTeamMembers(teamId, sorting);

  const pendingMemberIds = useRef<string[]>([]);

  const handlePendingChange = useCallback((ids: string[]) => {
    pendingMemberIds.current = ids;
  }, []);

  const handleSelectorClose = useCallback(
    async (open: boolean) => {
      if (open) return;
      const ids = pendingMemberIds.current;
      pendingMemberIds.current = [];
      if (ids.length === 0) return;
      await addTeamMembers(teamId, ids, "member");
      refetch();
    },
    [teamId, addTeamMembers, refetch],
  );

  const [removeTarget, setRemoveTarget] = useState<TeamMember | null>(null);

  const handleConfirmRemove = useCallback(async () => {
    if (!removeTarget) return;
    await removeTeamMembers(teamId, [removeTarget.userId]);
    setRemoveTarget(null);
    refetch();
  }, [removeTarget, teamId, removeTeamMembers, refetch]);

  const handleChangeRole = useCallback(
    async (member: TeamMember, newRole: TeamMembershipRole) => {
      if (member.role === newRole) return;
      await removeTeamMembers(teamId, [member.userId]);
      await addTeamMembers(teamId, [member.userId], newRole);
      refetch();
    },
    [teamId, removeTeamMembers, addTeamMembers, refetch],
  );

  const handleViewProfile = useCallback(
    (userId: string) => {
      push(`/settings/members/${userId}`);
    },
    [push],
  );

  const columns = useTeamMemberColumns({
    leadCount,
    onChangeRole: handleChangeRole,
  });
  const tableView = useTableViewSettings("team-members");

  const renderRowActions = useCallback(
    (member: TeamMember) => {
      const isLead = member.role === "lead";
      const isLastLead = isLead && leadCount <= 1;
      const isCurrentUser = member.userId === currentUser?.userId;

      return (
        <>
          <MenuItem
            disabled={isLastLead}
            onClick={() => handleChangeRole(member, isLead ? "member" : "lead")}
          >
            <Icon name="shield" size="lg" color="muted" />
            {isLead ? "Demote to Teammate..." : "Promote to Lead..."}
          </MenuItem>
          <MenuItem onClick={() => handleViewProfile(member.userId)}>
            <Icon name="user" size="lg" color="muted" />
            View profile
          </MenuItem>
          <MenuSeparator />
          <MenuItem
            disabled={isLastLead}
            onClick={() => setRemoveTarget(member)}
          >
            <Icon name={isCurrentUser ? "log-out" : "user"} size="lg" color="muted" />
            {isCurrentUser ? "Leave team..." : "Remove from team..."}
          </MenuItem>
        </>
      );
    },
    [leadCount, handleChangeRole, handleViewProfile, currentUser?.userId],
  );

  const { states, registerState, getGroupProps } = useGroupStates<TeamMember>();
  const groups = useMemo(
    () => [
      {
        key: "__all__",
        label: "All",
        totalCount: states.get("__all__")?.total,
        ...getGroupProps("__all__"),
      },
    ],
    [states, getGroupProps],
  );

  if (loading && teamMembers.length === 0) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (!loading && error) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Unable to load team</EmptyTitle>
            <EmptyDescription>{error.message}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </Box>
    );
  }

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader>
          <PageHeaderBreadcrumb>
            <BreadcrumbItem>
              {canManageTeams ? (
                // These crumbs navigate through the router, not an href, so the
                // link renders as a real button element for keyboard access.
                <BreadcrumbLink
                  render={<Box as="button" />}
                  onClick={() => replace("/settings/teams")}
                >
                  Teams
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage>Teams</BreadcrumbPage>
              )}
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              {canManageTeams ? (
                <BreadcrumbLink
                  render={<Box as="button" />}
                  onClick={() => replace(`/settings/teams/${teamId}`)}
                >
                  {teamName}
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage>{teamName}</BreadcrumbPage>
              )}
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Teammates</BreadcrumbPage>
            </BreadcrumbItem>
          </PageHeaderBreadcrumb>
          <PageHeaderActions>
            <DataTableColumnSettings
              columns={columns}
              {...tableView.settingsProps}
            />
            <UserSelector
              value={[]}
              onValueChange={handlePendingChange}
              onOpenChange={handleSelectorClose}
              exclude={existingUserIds}
            >
              <EntitySelectorTrigger
                render={
                  <Button size={isMobile ? "icon-xs" : "xs"}>
                    <Icon name="plus" />
                    {!isMobile && <Text>Add teammates</Text>}
                  </Button>
                }
              />
              <EntitySelectorContent size="sm" align="end">
                <EntitySelectorInput />
                <EntitySelectorPool value="users" />
                <EntitySelectorEmpty>No teammates found</EntitySelectorEmpty>
              </EntitySelectorContent>
            </UserSelector>
          </PageHeaderActions>
        </PageHeader>
        <Box gap="xl" paddingBottom="xl">
          <LocalGroupProvider<TeamMember>
            groupKey="__all__"
            rows={teamMembers}
            contextKey={`${teamId}|${JSON.stringify(sorting)}`}
            onStateChange={registerState}
          />
          <DataTable
            columns={columns}
            groups={groups}
            getRowId={(row) => row.userId}
            sorting={sorting}
            onSortingChange={setSorting}
            density="regular"
            fillColumnId="name"
            paddingX="md"
            {...tableView.tableProps}
            renderRowActions={renderRowActions}
            emptyState={
              <DataTableEmpty message="No teammates on this team yet" />
            }
          />
        </Box>

        <AlertDialog
          open={!!removeTarget}
          onOpenChange={(open) => { if (!open) setRemoveTarget(null); }}
        >
          <AlertDialogPopup>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {removeTarget?.userId === currentUser?.userId
                  ? "Leave team?"
                  : `Remove ${removeTarget?.name ?? "teammate"}?`}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {removeTarget?.userId === currentUser?.userId
                  ? `You will be removed from ${teamName}. You can be re-added later.`
                  : `${removeTarget?.name ?? "This teammate"} will be removed from ${teamName}. They can be re-added later.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogClose render={<Button variant="outline" />}>
                Cancel
              </AlertDialogClose>
              <Button variant="destructive" onClick={handleConfirmRemove}>
                {removeTarget?.userId === currentUser?.userId ? "Leave team" : "Remove from team"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogPopup>
        </AlertDialog>
      </ScrollArea>
    </Box>
  );
}

// Membership management is reachable by anyone with workspace-wide
// `manage.teams` OR a LEAD of this specific team — mirroring the backend
// `guardTeamMembership` gate (teams.resolver.ts). Rename/delete stay
// manage.teams-only on their own routes; this page does membership only,
// so a team lead landing here can't exceed what the server permits.
function TeamMembersRouteGuard() {
  const { teamId } = Route.useParams();
  const { currentUser, isLoading } = useAppData();
  const canManageTeams = checkPermission(
    currentUser?.permissions,
    "manage.teams",
  );
  // Only the lead path needs the team's leads — skip the lookup entirely
  // for manage.teams holders (who already pass) so the common case stays
  // a single render with no extra query.
  const { team, loading } = useTeamDetail(teamId, { skip: canManageTeams });

  if (isLoading || !currentUser) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }
  if (canManageTeams) return <TeamMembersPage />;
  if (loading && !team) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }
  const isLead = team?.viewerRole === "lead";
  if (!isLead) return <AccessDenied />;
  return <TeamMembersPage />;
}

export const Route = createFileRoute(
  "/(app)/$workspace/settings/teams/$teamId/members",
)({ component: TeamMembersRouteGuard });
